import React from 'react';
import type { Theme } from '../../types';

/**
 * #574 (V6.39.0): ein Knopf für die ganze Verwaltung — 40 px hoch, Schrift 600, Zweitknopf durchsichtig mit Rahmen
 * (docs/mockups/fenster-knoepfe/entscheidungen.md). Varianten:
 *  - `primary` Hauptknopf (Akzent) · `secondary` Zweitknopf (Rahmen) · `danger` Gefahr (rot)
 *  - `ghost` leiser Textknopf · `symbol` quadratischer Knopf nur mit Symbol (braucht `label`)
 *  - `dangerOutline` leise Gefahr mit Rahmen (z. B. „Entfernen“ neben „Ändern“) · `superAdmin` Lila, nur für Super-Admin-Dinge
 *    (Seiten-Knöpfe, docs/mockups/seiten-knoepfe/entscheidungen.md)
 * `klein` für dichte Leisten (32 px).
 */
export type ButtonVariante = 'primary' | 'secondary' | 'danger' | 'dangerOutline' | 'superAdmin' | 'ghost' | 'symbol';

export const Button: React.FC<{
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  variant?: ButtonVariante | string;
  children: React.ReactNode;
  style?: React.CSSProperties;
  type?: 'button' | 'submit';
  theme: Pick<Theme, 'accent' | 'border' | 'text' | 'textSec' | 'danger'>;
  klein?: boolean;
  /** Beschriftung für Screenreader/Tooltip — Pflicht bei `symbol` */
  label?: string;
  title?: string;
  autoFocus?: boolean;
  /** Gehört zu diesem Formular, auch wenn der Knopf außerhalb steht (Fußleiste der Fenster-Hülle) */
  form?: string;
}> = ({ onClick, disabled, variant = 'primary', children, style, type = 'button', theme, klein, label, title, autoFocus, form }) => {
  const hoehe = klein ? 32 : 40;
  const base: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px', minHeight: hoehe,
    padding: klein ? '5px 12px' : '9px 16px', fontSize: klein ? '0.85rem' : '0.9rem', fontWeight: 600, lineHeight: 1.25,
    border: '1px solid transparent', borderRadius: 'var(--r-m)', cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1, fontFamily: 'inherit', whiteSpace: 'nowrap',
  };
  const variants: Record<string, React.CSSProperties> = {
    primary: { backgroundColor: theme.accent, borderColor: theme.accent, color: '#fff' },
    secondary: { backgroundColor: 'transparent', borderColor: theme.border, color: theme.text },
    danger: { backgroundColor: theme.danger, borderColor: theme.danger, color: '#fff' },
    dangerOutline: { backgroundColor: 'transparent', borderColor: theme.danger, color: theme.danger },
    // Lila = Super-Admin, wie das Rollen-Abzeichen (#7c3aed)
    superAdmin: { backgroundColor: 'transparent', borderColor: '#7c3aed', color: '#7c3aed' },
    ghost: { backgroundColor: 'transparent', color: theme.accent },
    symbol: { backgroundColor: 'transparent', borderColor: theme.border, color: theme.textSec, padding: 0, width: hoehe, minWidth: hoehe, flexShrink: 0 },
  };
  return (
    <button type={type} form={form} onClick={onClick} disabled={disabled} aria-label={label} title={title ?? label} autoFocus={autoFocus}
      style={{ ...base, ...(variants[variant] ?? variants.primary), ...style }}>
      {children}
    </button>
  );
};
